/**
 * PostHog Analytics Provider Implementation
 * 
 * Wraps the PostHog SDK to conform to our AnalyticsProvider interface.
 * Can be swapped out for any other provider (Mixpanel, Amplitude, etc.)
 * 
 * Features:
 * - Group analytics (households)
 * - Super properties (app version, environment, platform)
 * - Session replay (disabled for admins)
 */

import posthog from 'posthog-js';
import { AnalyticsProvider } from './AnalyticsProvider';

export class PostHogProvider extends AnalyticsProvider {
  constructor() {
    super();
    this.initialized = false;
    this.isAdmin = false;
  }

  init(config) {
    if (this.initialized) {
      console.warn('PostHog already initialized');
      return;
    }

    const { apiKey, host, options = {}, isAdmin = false } = config;
    this.isAdmin = isAdmin;

    if (!apiKey) {
      console.warn('PostHog API key not provided, analytics disabled');
      return;
    }

    posthog.init(apiKey, {
      api_host: host || 'https://us.i.posthog.com',
      capture_pageview: true,
      capture_pageleave: true,
      persistence: 'localStorage',
      autocapture: false, // We'll track events explicitly for cleaner data
      // Session replay - disabled for admins to avoid recording sensitive admin actions
      disable_session_recording: isAdmin,
      session_recording: {
        maskAllInputs: true, // Mask all input fields for privacy
        maskTextSelector: '.sensitive-data', // Additional sensitive data masking
      },
      ...options,
    });

    // Register super properties that persist across all events
    posthog.register({
      app_version: process.env.REACT_APP_VERSION || '1.0.0',
      environment: process.env.NODE_ENV,
      platform: 'web',
    });

    this.initialized = true;
    console.log('PostHog analytics initialized', { isAdmin, sessionRecording: !isAdmin });
  }

  /**
   * Update session recording state (e.g., when user role changes)
   */
  updateSessionRecording(isAdmin) {
    if (!this.isEnabled()) return;
    this.isAdmin = isAdmin;
    
    if (isAdmin) {
      posthog.stopSessionRecording();
    } else {
      posthog.startSessionRecording();
    }
  }

  identify(userId, properties = {}) {
    if (!this.isEnabled()) return;

    posthog.identify(userId, properties);
  }

  reset() {
    if (!this.isEnabled()) return;

    posthog.reset();
  }

  track(eventName, properties = {}) {
    if (!this.isEnabled()) return;

    posthog.capture(eventName, {
      ...properties,
      timestamp: new Date().toISOString(),
    });
  }

  page(pageName, properties = {}) {
    if (!this.isEnabled()) return;

    posthog.capture('$pageview', {
      page_name: pageName,
      ...properties,
    });
  }

  setUserProperties(properties) {
    if (!this.isEnabled()) return;

    posthog.people.set(properties);
  }

  /**
   * Associate user with a group (e.g., household)
   * @param {string} groupType - Type of group (e.g., 'household')
   * @param {string} groupId - Unique identifier for the group
   * @param {Object} properties - Optional group properties
   */
  group(groupType, groupId, properties = {}) {
    if (!this.isEnabled()) return;

    posthog.group(groupType, groupId, properties);
  }

  isEnabled() {
    return this.initialized;
  }

  /**
   * Get the underlying PostHog instance for advanced usage
   * @returns {posthog}
   */
  getInstance() {
    return posthog;
  }
}
